/**
 * Admin Service
 * 
 * Handles all admin-related API calls for template management and user deletion management.
 */

import api from './api';
import type { UserPendingDeletion } from '@budgira/shared';

interface PaymentMethodTemplate {
  id: string;
  name: string;
  logoUrl: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const adminService = {
  /**
   * Get all payment method templates (admin only)
   */
  async getPaymentMethodTemplates(): Promise<PaymentMethodTemplate[]> {
    const response = await api.get<ApiResponse<PaymentMethodTemplate[]>>('/admin/payment-method-templates');
    return response.data.data;
  },

  /**
   * Create a new payment method template
   */
  async createPaymentMethodTemplate(data: { name: string; logoUrl: string }): Promise<PaymentMethodTemplate> {
    const response = await api.post<ApiResponse<PaymentMethodTemplate>>('/admin/payment-method-templates', data);
    return response.data.data;
  },

  /**
   * Update a payment method template
   */
  async updatePaymentMethodTemplate(id: string, data: { name?: string; logoUrl?: string }): Promise<PaymentMethodTemplate> {
    const response = await api.put<ApiResponse<PaymentMethodTemplate>>(`/admin/payment-method-templates/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete a payment method template
   */
  async deletePaymentMethodTemplate(id: string): Promise<void> {
    await api.delete(`/admin/payment-method-templates/${id}`);
  },

  // ============================================
  // ACCOUNT DELETION MANAGEMENT
  // ============================================

  /**
   * Get all users with pending deletion requests
   */
  async getUsersPendingDeletion(): Promise<UserPendingDeletion[]> {
    const response = await api.get<ApiResponse<UserPendingDeletion[]>>('/admin/users/pending-deletion');
    return response.data.data;
  },

  /**
   * Reactivate a user account that was pending deletion
   */
  async reactivateUser(userId: string): Promise<void> {
    await api.post(`/admin/users/${userId}/reactivate`);
  },

  /**
   * Permanently delete a user account (only after grace period)
   */
  async permanentlyDeleteUser(userId: string): Promise<void> {
    await api.delete(`/admin/users/${userId}/permanent`);
  },
};

export default adminService;
